/**
 * Build a filesystem-safe download/display filename from the app document name,
 * revision number, and the original upload extension.
 *
 * Example: ("A-401 Finishes", "Drawings.pdf", 1) → "A-401-Finishes-R1.pdf"
 */
const WINDOWS_RESERVED_NAMES = new Set([
  "CON",
  "PRN",
  "AUX",
  "NUL",
  ...Array.from({ length: 9 }, (_, index) => `COM${index + 1}`),
  ...Array.from({ length: 9 }, (_, index) => `LPT${index + 1}`),
]);

export function buildDocumentDownloadFilename(
  documentName: string | null | undefined,
  originalFilename: string | null | undefined,
  revisionNumber?: number | null
): string {
  const extension = sanitizeExtension(getExtension(originalFilename));
  const base = toDownloadBasename(documentName);
  const stem = escapeWindowsReservedName(stripMatchingExtension(base, extension));
  const withRevision =
    typeof revisionNumber === "number" && Number.isFinite(revisionNumber)
      ? `${stem}-R${revisionNumber}`
      : stem;
  return `${withRevision}.${extension}`;
}

/**
 * Fetch a URL and trigger a browser download using the provided filename.
 */
export async function downloadUrlAsFile(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download ${filename}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function sanitizeExtension(extension: string): string {
  const sanitized = extension.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!sanitized || sanitized.length > 10) {
    return "pdf";
  }
  return sanitized;
}

function escapeWindowsReservedName(stem: string): string {
  if (WINDOWS_RESERVED_NAMES.has(stem.toUpperCase())) {
    return `_${stem}`;
  }
  return stem;
}

function getExtension(originalFilename: string | null | undefined): string {
  const trimmed = (originalFilename ?? "").trim();
  const lastDot = trimmed.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === trimmed.length - 1) {
    return "";
  }
  return trimmed.slice(lastDot + 1).toLowerCase();
}

function stripMatchingExtension(basename: string, extension: string): string {
  const suffix = `.${extension}`;
  if (basename.toLowerCase().endsWith(suffix)) {
    return basename.slice(0, -suffix.length);
  }
  return basename;
}

function toDownloadBasename(documentName: string | null | undefined): string {
  const normalized = (documentName ?? "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[<>:"/\\|?*]+/g, "")
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32;
    })
    .join("")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "document";
}
